/**
 * Модалка группового выполнения событий
 * Позволяет заполнить атрибуты для нескольких событий одновременно
 */
/** @jsxImportSource @emotion/react */
import React, { useState, useMemo } from 'react';
import { Modal, Form, Input, DatePicker, Button, Space, Alert, Typography, Spin } from 'antd';
import dayjs from 'dayjs';
import { skipToken } from '@reduxjs/toolkit/query';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import { useCompleteEventMutation } from '../../api/plantingEventsApi';
import { useGetEventTemplateByIdQuery } from '../../api/eventTemplatesApi';
import { toastError, toastSuccess } from '../../utils/toast';
import type { BatchChildEvent } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';
import { AttributeInput } from './AttributeInput';
import { useTranslation } from 'react-i18next';
import { datePickerFormat } from '../../i18n';

const { TextArea } = Input;
const { Text } = Typography;

const styles = {
  loadingContainer: css({ marginTop: 16 }),
};

interface BulkCompleteEventsModalProps {
  open: boolean;
  events: BatchChildEvent[];
  templateId: number;
  templateName: string;
  farmId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkCompleteEventsModal: React.FC<BulkCompleteEventsModalProps> = ({
  open,
  events,
  templateId,
  templateName,
  farmId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('events');
  const save = useSave();
  const [form] = Form.useForm();
  const [completeEvent] = useCompleteEventMutation();
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Загружаем шаблон для получения атрибутов
  const { data: templateDetail, isLoading: loadingTemplate } = useGetEventTemplateByIdQuery(
    templateId && open && save?.id ? { id: templateId, saveId: save.id } : skipToken
  );

  // Получаем атрибуты из шаблона
  const attributes = useMemo(() => {
    if (loadingTemplate) return [];
    if (templateDetail?.properties && templateDetail.properties.length > 0) {
      return templateDetail.properties.map((attr) => ({
        propertyId: attr.propertyId,
        name: attr.name,
        dataType: attr.dataType,
        unit: attr.unit,
        isRequired: attr.isRequired,
      }));
    }
    return [];
  }, [templateDetail, loadingTemplate]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setProcessing(true);

      let successCount = 0;
      let errorCount = 0;

      // Последовательно выполняем события
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        setProgress({ current: i + 1, total: events.length });

        try {
          // Формируем payload для каждого события
          const attributesPayload: { [key: string]: number | string | boolean } = {};
          
          attributes.forEach((attr) => {
            const fieldValue = values[`attr_${attr.propertyId}`];
            if (fieldValue !== undefined && fieldValue !== null) {
              attributesPayload[String(attr.propertyId)] = fieldValue;
            }
          });

          const payload = {
            actualDate: values.actualDate
              ? dayjs(values.actualDate).format('YYYY-MM-DD')
              : dayjs().format('YYYY-MM-DD'),
            notes: values.notes || undefined,
            attributes: Object.keys(attributesPayload).length > 0 ? attributesPayload : undefined,
          };

          await completeEvent({
            saveId: save.id,
            eventId: event.id,
            payload,
          }).unwrap();

          successCount++;
        } catch (error) {
          console.error(`Error completing event ${event.id}:`, error);
          errorCount++;
        }
      }

      setProcessing(false);

      if (errorCount === 0) {
        toastSuccess(t('bulkComplete.success', { count: successCount }));
      } else {
        toastError(
          t('bulkComplete.partial', { count: successCount, errors: errorCount })
        );
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error in bulk complete:', error);
      setProcessing(false);
      toastError(t('bulkComplete.failed'));
    }
  };

  return (
    <Modal
      open={open}
      title={t('bulkComplete.title', { name: templateName })}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={processing}>
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={processing}
          disabled={loadingTemplate}
        >
          {processing
            ? t('bulkComplete.running', { current: progress.current, total: progress.total })
            : t('bulkComplete.submit', { count: events.length })}
        </Button>,
      ]}
    >
      {loadingTemplate ? (
        <div css={css([commonStyles.textCenter, { padding: '40px' }])}>
          <Spin size="large" />
          <div css={styles.loadingContainer}>{t('bulkComplete.loadingTemplate')}</div>
        </div>
      ) : (
        <Space direction="vertical" size="middle" css={commonStyles.fullWidth}>
          <Alert
            type="info"
            message={t('bulkComplete.selected', { count: events.length })}
            description={t('bulkComplete.sameValuesHint')}
            showIcon
          />

          <Form form={form} layout="vertical">
            {/* Фактическая дата */}
            <Form.Item
              label={t('bulkComplete.actualDate')}
              name="actualDate"
              initialValue={dayjs()}
              rules={[{ required: true, message: t('form.dateRequired') }]}
            >
              <DatePicker css={commonStyles.fullWidth} format={datePickerFormat()} />
            </Form.Item>

            {/* Атрибуты шаблона */}
            {attributes.map((attr) => (
              <Form.Item
                key={attr.propertyId}
                label={
                  <Space>
                    <Text>{attr.name}</Text>
                    {attr.unit && <Text type="secondary">({attr.unit})</Text>}
                  </Space>
                }
                name={`attr_${attr.propertyId}`}
                rules={[
                  {
                    required: attr.isRequired,
                    message: t('form.fillField', { name: attr.name }),
                  },
                ]}
              >
                <AttributeInput dataType={attr.dataType} />
              </Form.Item>
            ))}

            {/* Примечания */}
            <Form.Item label={t('form.notes')} name="notes">
              <TextArea rows={3} placeholder={t('bulkComplete.notesPlaceholder')} />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Modal>
  );
};
